import { API_CONFIG, apiUtils } from '../config.js';

// Utility function to normalize fee response
const normalizeFeeResponse = (fee) => {
  if (!fee) return null;
  
  return {
    ...fee,
    breakdown: fee.breakdown?.map(cat => ({
      ...cat,
      categoryName: cat.categoryName || cat.name,
      amount: cat.amount || 0,
      paid: cat.paid || 0,
      dueDate: cat.dueDate,
      status: cat.status || 'Due',
      paymentDetails: cat.paymentDetails || [],
    })) || [],
    totalAmount: fee.totalAmount || 0,
    totalPaid: fee.totalPaid || 0,
    balance: fee.balance || (fee.totalAmount - fee.totalPaid) || 0,
  };
};

// Fee Management API
export const feeApi = {

  // fee strucure
  createFeeStructure: async (feeStructureData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/createFeeStructure`;
    return apiUtils.makeAuthenticatedRequest(url, { 
      method: 'POST',
      body: JSON.stringify(feeStructureData),
    });
  },

  getFeeStructures: async () => {
    const url = `${API_CONFIG.BASE_URL}/fee/structures`;
    return apiUtils.makeAuthenticatedRequest(url,{
      method: 'GET',
    })
  },

  deleteFeeStructure: async(feeStructureId)=>{
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/fee/feeStructure/${feeStructureId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  updateFeeStructure: async(feeStructureId,feeStructureData)=>{
     const url = `${API_CONFIG.BASE_URL}/fee/updateFeeStructure/${feeStructureId}`;
    return apiUtils.makeAuthenticatedRequest(url, { 
      method: 'PUT',
      body: JSON.stringify(feeStructureData),
    }); 
  },

    // Assign students to fee structure
    // data should contain: { feeStructureId, classes: [{ classId, studentEnrollmentIds: [...] }] }
    assignStudentsToFeeStructure: async (data) => {
      const url = `${API_CONFIG.BASE_URL}/fee/assignStudents`;
      const body = {
        feeStructureId: data.feeStructureId,
        classes: data.classes || [], // Expected format: [{ classId, studentEnrollmentIds: [...] }]
      };

      return apiUtils.makeAuthenticatedRequest(url, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

  // assign students to fee strucutre

  // Create fee
  // feeData should contain: { studentEnrollmentId, amount, dueDate }
  createFee: async (feeData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/create`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(feeData),
    });
  },

  // Record payment (new endpoint from documentation)
  // paymentData should contain: { studentEnrollmentId, categoryName, amount, paymentDate, paymentMode, transactionId, paymentGateway }
  recordPayment: async (paymentData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/record-payment`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  // Mark fee as paid (legacy)
  // paymentData should contain: { studentEnrollmentId, paidDate, transactionId, paymentMethod }
  markFeeAsPaid: async (paymentData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/mark-paid`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  // Get student fees
  getStudentFees: async (studentRegistrationId, fromDate, toDate) => {
    const queryParams = apiUtils.buildQueryString({ from: fromDate, to: toDate });
    const url = `${API_CONFIG.BASE_URL}/fee/student/${studentRegistrationId}?${queryParams}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = Array.isArray(res.data) ? res.data.map(normalizeFeeResponse) : normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Get students by fee structure
  getStudentsByStructure: async (feeStructureId, academicYear) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/fee/students/by-structure/${feeStructureId}${params}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = Array.isArray(res.data) ? res.data.map(normalizeFeeResponse) : res.data;
    }
    return res;
  },

  // Get all fee records with filters
  getAllRecords: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/fee/records${queryParams ? `?${queryParams}` : ''}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = Array.isArray(res.data) ? res.data.map(normalizeFeeResponse) : res.data;
    }
    return res;
  },

  // Get fee record by enrollment
  getRecordByEnrollment: async (enrollmentId, academicYear) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/fee/record/enrollment/${enrollmentId}${params}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Get pending fees
  getPendingFees: async (classId, academicYear) => {
    // Backend expects 'unitId' as the query param name
    const params = {};
    if (classId) params.unitId = classId;
    if (academicYear) params.academicYear = academicYear;
    const queryParams = apiUtils.buildQueryString(params);
    const url = `${API_CONFIG.BASE_URL}/fee/pending${queryParams ? `?${queryParams}` : ''}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = Array.isArray(res.data) ? res.data.map(normalizeFeeResponse) : normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Refresh overdue fees
  refreshOverdueFees: async (asOfDate) => {
    const url = `${API_CONFIG.BASE_URL}/fee/refresh-overdue`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify({ asOf: asOfDate }),
    });
  },

  // Get fee statistics
  getFeeStatistics: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/fee/statistics?${queryParams}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // Get fee by ID
  getFeeById: async (feeId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/get-by-id/${feeId}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Update fee
  updateFee: async (feeId, feeData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/update/${feeId}`;
    const res = await apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(feeData),
    });
    if (res?.success && res?.data) {
      res.data = normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Delete fee
  deleteFee: async (feeId) => {
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}/fee/delete/${feeId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : { success: true };
    } catch (error) {
      apiUtils.handleError(error);
      throw error;
    }
  },

  // --- Fee Reminders & Campaigns ---

  getReminderTemplates: async () => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/templates`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  createReminderTemplate: async (templateData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/templates`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  },

  updateReminderTemplate: async (templateId, templateData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/templates/${templateId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  },

  deleteReminderTemplate: async (templateId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/templates/${templateId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'DELETE',
    });
  },

  getReminderCampaigns: async () => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/campaigns`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  createReminderCampaign: async (campaignData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/campaigns`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
  },

  sendReminderCampaign: async (campaignId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/send`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify({ campaignId }),
    });
  },

  // Direct send fee reminder (one-click)
  sendDirectReminder: async (feeStructureId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/send-direct`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify({ feeStructureId }),
    });
  },

  updateReminderCampaign: async (campaignId, campaignData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/campaigns/${campaignId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    });
  },

  deleteReminderCampaign: async (campaignId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/reminders/campaigns/${campaignId}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'DELETE',
    });
  },

  // --- Fine Management ---

  addFineRules: async (feeStructureId, rulesData) => {
    const url = `${API_CONFIG.BASE_URL}/fee/structure/${feeStructureId}/fine-rules`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(rulesData),
    });
  },

  // Get assigned classes for a fee structure
  getAssignedClasses: async (feeStructureId, academicYear) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/fee/structure/${feeStructureId}/assigned-classes${params}`;
    return apiUtils.makeAuthenticatedRequest(url);
  },

  // --- Receipts ---

  getReceiptPdf: async (transactionId) => {
    try {
      const url = `${API_CONFIG.BASE_URL}/fee/receipt/${transactionId}/pdf`;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/pdf'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download receipt' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      return response.blob();
    } catch (error) {
      console.error('Error downloading receipt:', error);
      throw error;
    }
  },

  sendReceiptWhatsapp: async (transactionId) => {
    const url = `${API_CONFIG.BASE_URL}/fee/receipt/${transactionId}/whatsapp`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
    });
  },

  // Get fee reports
  getFeeReports: async (filters = {}) => {
    const queryParams = apiUtils.buildQueryString(filters);
    const url = `${API_CONFIG.BASE_URL}/fee/reports?${queryParams}`;
    const res = await apiUtils.makeAuthenticatedRequest(url);
    if (res?.success && res?.data) {
      res.data = Array.isArray(res.data) ? res.data.map(normalizeFeeResponse) : normalizeFeeResponse(res.data);
    }
    return res;
  },

  // Send pending fee reminders via WhatsApp
  sendPendingFeeReminders: async (academicYear, phoneNumberId) => {
    const params = academicYear ? `?academicYear=${academicYear}` : '';
    const url = `${API_CONFIG.BASE_URL}/fee/whatsapp/pending${params}`;
    return apiUtils.makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify({ phoneNumberId }),
    });
  },

  // Get timeline analytics from external service
  getTimelineAnalytics: async (params) => {
     const baseUrl = "https://whatsapp-chatbot-yehh.onrender.com";
     const queryString = apiUtils.buildQueryString(params);
     const url = `${baseUrl}/analytics/timeline?${queryString}`;
     
     return apiUtils.makeAuthenticatedRequest(url, {
       method: 'GET',
     });
  },
};

export default feeApi;
